import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { SettingsPage } from "@/components/v2/settings-page";
import { SettingsTable } from "@/components/v2/settings-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BusinessDataActionItems } from "@/components/business-data-actions";
import {
  useAllBusinesses,
  type AdminBusiness,
} from "@/hooks/use-all-businesses";
import { useAllAccounts } from "@/hooks/use-admin";

function statusVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  if (status === "active") return "default";
  if (status === "inactive") return "outline";
  return "secondary";
}

function AccountBusinessActions({ business }: { business: AdminBusiness }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <MoreHorizontal className="h-4 w-4" aria-hidden />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <BusinessDataActionItems business={business} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function V2AdvaAccountBusinessesPage() {
  const { id: accountId } = useParams<{ id: string }>();
  const { data: businessesData, isLoading: businessesLoading } =
    useAllBusinesses();
  const { data: accountsData } = useAllAccounts();

  const account = useMemo(
    () => accountsData?.accounts.find((a) => a.id === accountId),
    [accountsData, accountId],
  );

  const businesses = useMemo(() => {
    const all = businessesData?.businesses ?? [];
    return all.filter((b) => b.account_id === accountId);
  }, [businessesData, accountId]);

  const accountName = account?.name ?? "Account";

  return (
    <SettingsPage
      breadcrumbs={[
        { label: "Adva" },
        { label: "Accounts", href: "/settings/adva/accounts" },
        { label: accountName },
        { label: "Businesses" },
      ]}
      title={`${accountName} — Businesses`}
      subtitle="Manage businesses under this account. Backup, restore, and reset operate on the selected business after switching."
    >
      <SettingsTable
        colSpan={4}
        isLoading={businessesLoading}
        isEmpty={businesses.length === 0}
        emptyMessage="No businesses found for this account."
      >
        <TableHeader>
          <TableRow className="h-9">
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Name
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Slug
            </TableHead>
            <TableHead className="typo-label text-[color:var(--color-text-md)] px-4">
              Status
            </TableHead>
            <TableHead className="w-16 px-4" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {businesses.map((business) => (
            <TableRow key={business.id}>
              <TableCell className="px-4 py-2 typo-body font-medium text-[color:var(--color-text-hi)]">
                {business.name}
              </TableCell>
              <TableCell className="px-4 py-2 typo-body text-xs font-mono text-[color:var(--color-text-lo)]">
                {business.slug}
              </TableCell>
              <TableCell className="px-4 py-2">
                <Badge variant={statusVariant(business.status)}>
                  {business.status}
                </Badge>
              </TableCell>
              <TableCell className="px-4 py-2">
                <AccountBusinessActions business={business} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </SettingsTable>
    </SettingsPage>
  );
}
